import { createContext, ReactNode, useContext } from 'react'

import { HeaderActionButton } from '@/components/new/table/header/HeaderActions'
import { FilterTable } from './filter/FiltersRow'
import { ColumnDef, Row } from '@tanstack/react-table'
import { AdvancedFiltersList } from '@/components/new/table/filter/AdvancedFilters'

export interface QuickFiltersList {
  list?: FilterTable[]
  onChange: (_newFilters: Record<string, string>) => void
}

export type TableColumn<TData = unknown, TValue = unknown> = ColumnDef<
  TData,
  TValue
> &
  ColumnsAditionalProps

// interface DataTableProps<TData, TValue> {
//   columns: TableColumn<TData, TValue>[]
//   storageKey: string
// }

interface ColumnsAditionalProps {
  skeletonWidth?: string //Default value: '80%'
  defaultVisibilityMobile?: boolean // Default value: true
}

interface Props<TData> {
  data: TData[]
  isLoading: boolean
  refetch?: () => void
  paginate?: {
    currentPage: number
    totalItems: number
    pageSize?: number
    goToPage: (_page: number) => void
    goToNextPage: () => void
    goToPreviousPage: () => void
  }
  onRowClick?: (_row: Row<TData>, _event: React.MouseEvent) => void
  leftButtons?: HeaderActionButton[]
  rightButtons?: HeaderActionButton[]
  showColumnsDropdown?: boolean
  quickFilterList?: QuickFiltersList
  search?: {
    placeholder?: string
    action: (_searchQuery: string) => void
  }
  advancedFilterList?: AdvancedFiltersList
}

// Define the context type
interface TableContextType<TData> {
  data: TData[]
  isLoading: boolean
  refetch?: () => void
  // Add direct access to pagination methods for convenience
  paginate?: {
    currentPage: number
    totalItems: number
    pageSize: number
    goToPage: (_page: number) => void
    goToNextPage: () => void
    goToPreviousPage: () => void
    totalPages: number
  }
  // Skeleton data for loading state
  skeletonRowCount: number
  onRowClick?: (_row: Row<TData>, _event: React.MouseEvent) => void
  leftButtons?: HeaderActionButton[]
  rightButtons?: HeaderActionButton[]
  showColumnsDropdown?: boolean
  quickFilterList?: QuickFiltersList
  advancedFilterList?: AdvancedFiltersList
  search?: {
    action: (_searchQuery: string) => void
    placeholder?: string
  }
}
// Create the context with a default value
const TableContext = createContext<TableContextType<unknown> | undefined>(
  undefined
)

// Default values
const DEFAULT_PAGE_SIZE = 30

// Provider component that will wrap the table components
export const TableContextProvider = <TData,>({
  data,
  isLoading,
  refetch,
  paginate,
  onRowClick,
  leftButtons,
  rightButtons,
  showColumnsDropdown,
  quickFilterList,
  search,
  advancedFilterList,
  children,
}: Props<TData> & { children: ReactNode }) => {
  const contextValue: TableContextType<TData> = {
    data: data,
    isLoading: isLoading,
    refetch: refetch,
    onRowClick: onRowClick,
    paginate: paginate
      ? {
          totalItems: paginate.totalItems,
          pageSize: paginate?.pageSize ?? DEFAULT_PAGE_SIZE,
          currentPage: paginate.currentPage,
          goToPage: paginate.goToPage,
          goToNextPage: paginate.goToNextPage,
          goToPreviousPage: paginate.goToPreviousPage,
          totalPages: Math.ceil(
            paginate.totalItems / (paginate?.pageSize ?? DEFAULT_PAGE_SIZE)
          ),
        }
      : undefined,
    skeletonRowCount: data.length,
    leftButtons: leftButtons,
    rightButtons: rightButtons,
    showColumnsDropdown: showColumnsDropdown,
    quickFilterList: quickFilterList,
    search: search,
    advancedFilterList: advancedFilterList,
  }

  return (
    <TableContext.Provider
      value={contextValue as unknown as TableContextType<unknown>}
    >
      {children}
    </TableContext.Provider>
  )
}

// Custom hook to use the table context
export const useTableContext = <TData,>() => {
  const context = useContext(TableContext) as
    | TableContextType<TData>
    | undefined
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}
